import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Trash2, FileText, Gavel, Pencil, Camera, Link2 } from "lucide-react";
import { MessageList } from "./Messages";
import { toast } from "sonner";
import { API, fmtErr, money, fmtDate } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { useAuth, isManagerUp } from "../../lib/auth";
import { printDoc } from "../../lib/printDoc";
import { ContractDoc, BillDoc, ReceiptDoc } from "../../components/docs";
import { ContractTermsFields, EMPTY_TERMS, termsPayload, termsFromLoan } from "../../components/ContractTermsFields";
import { LetterDialog } from "../../components/PrintDialog";
import { StatusBadge } from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const EMPTY_LOAN = { customer_id: "", principal: "", monthly_interest_rate: 10, term_months: 3, notes: "" };
const EMPTY_ITEM = { name: "", category: "Jewelry", estimated_value: "", condition: "", storage_location: "", serial_no: "", brand: "", model: "", year: "", color: "", plate_no: "", chassis_no: "", fuel_percent: "", jewelry_type: "", gold_karat: "", weight_grams: "", engine_no: "", dntt_ref: "", imei: "", data_wiped: false, backup_done: false, operating_hours: "", last_maintenance: "", accessories: "" };
const JEWELRY_TYPES = ["Ring", "Necklace", "Bracelet", "Earrings", "Chain", "Pendant", "Other"];
const KARATS = ["24K", "22K", "21K", "18K", "14K", "10K"];
const VEHICLE_CATS = ["Car", "Motorcycle", "Heavy Equipment"];
const CATS = ["Jewelry", "Watches", "Electronics", "Car", "Motorcycle", "Heavy Equipment", "Tools", "Other"];
const CATEGORY_RATES = { Jewelry: 10, Watches: 10, Car: 10, Motorcycle: 10, "Heavy Equipment": 10, Electronics: 15 };

export default function Loans() {
  const { t } = useLang();
  const { user } = useAuth();
  const managerUp = isManagerUp(user);
  const [params, setParams] = useSearchParams();
  const [shopCfg, setShopCfg] = useState(null);
  useEffect(() => { API.get("/shop/me").then((r) => setShopCfg(r.data.shop)).catch(() => {}); }, []);
  useEffect(() => {
    if (shopCfg?.interest_default_rate) {
      setForm((f) => (!f.customer_id && Number(f.monthly_interest_rate) === 10
        ? { ...f, monthly_interest_rate: shopCfg.interest_default_rate } : f));
    }
    // eslint-disable-next-line
  }, [shopCfg]);
  const [loans, setLoans] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [regime, setRegime] = useState("all");
  const [borrower, setBorrower] = useState("all");
  const [addendum, setAddendum] = useState(null);
  const [loanMessages, setLoanMessages] = useState([]);
  const copyPortal = async () => {
    try {
      const { data } = await API.get(`/shop/loans/${detail.id}/portal-link`);
      try { await navigator.clipboard.writeText(data.url); toast.success(t("portal.copied")); } catch { window.prompt(t("portal.copy"), data.url); }
    } catch (err) { toast.error(fmtErr(err)); }
  };
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_LOAN);
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [terms, setTerms] = useState(EMPTY_TERMS);
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsDraft, setTermsDraft] = useState(EMPTY_TERMS);
  const [detail, setDetail] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  const [payType, setPayType] = useState("custom");
  const [payNote, setPayNote] = useState("");
  const [paySummary, setPaySummary] = useState(null);
  const [contract, setContract] = useState(null);
  const [editContract, setEditContract] = useState(false);
  const [contractDraft, setContractDraft] = useState(null);
  const [printMode, setPrintMode] = useState("a4");
  const [draftMark, setDraftMark] = useState(false);
  const [bundle, setBundle] = useState(null);
  const [bill, setBill] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => API.get("/shop/loans", { params: {
    ...(filter !== "all" ? { status: filter } : {}),
    ...(regime !== "all" ? { item_use: regime } : {}),
    ...(borrower !== "all" ? { borrower_kind: borrower } : {}),
  } }).then((r) => setLoans(r.data)).catch(() => {});
  useEffect(() => {
    load();
    API.get("/shop/customers").then((r) => setCustomers(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [filter, regime, borrower]);

  const openAddendum = async () => {
    try {
      const { data } = await API.get(`/shop/loans/${detail.id}/extension-addendum`);
      setAddendum(data);
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  useEffect(() => {
    const openId = params.get("open");
    if (openId) openDetail(openId);
    // eslint-disable-next-line
  }, [params]);

  const openDetail = (id) => API.get(`/shop/loans/${id}`).then((r) => { setDetail(r.data); setPayAmount(""); setPayNote(""); setPayType("custom"); API.get("/shop/messages", { params: { loan_id: id, limit: 50 } }).then((m) => setLoanMessages(m.data)).catch(() => setLoanMessages([])); }).catch(() => {});
  const closeDetail = () => { setDetail(null); if (params.get("open")) setParams({}); };

  const openBundle = async () => {
    setBusy(true);
    try {
      const [c, b] = await Promise.all([
        API.get(`/shop/loans/${detail.id}/contract`),
        API.get(`/shop/loans/${detail.id}/bill`),
      ]);
      let receipt = null;
      try {
        const pays = await API.get("/shop/repayments");
        const last = (pays.data || []).find((p) => p.loan_id === detail.id);
        if (last) receipt = (await API.get(`/shop/repayments/${last.id}/receipt`)).data;
      } catch { /* receipt optional */ }
      setBundle({ contract: c.data, bill: b.data, receipt });
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const startContractEdit = () => {
    setContractDraft({ articles: contract.articles.map((a) => ({ ...a })), terms: [...contract.additional_terms_en] });
    setEditContract(true);
  };
  const setDraftArticle = (no, field, value) =>
    setContractDraft((d) => ({ ...d, articles: d.articles.map((a) => (a.no === no ? { ...a, [field]: value } : a)) }));
  const setDraftTerm = (i, value) =>
    setContractDraft((d) => ({ ...d, terms: d.terms.map((x, j) => (j === i ? value : x)) }));
  const reloadContract = async () => {
    const { data } = await API.get(`/shop/loans/${contract.loan.id}/contract`);
    setContract(data);
  };
  const saveContract = async () => {
    setBusy(true);
    try {
      await API.patch(`/shop/loans/${contract.loan.id}/contract`, {
        articles: contractDraft.articles, additional_terms_en: contractDraft.terms,
      });
      toast.success(t("msg.saved"));
      await reloadContract();
      setEditContract(false);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };
  const resetContract = async () => {
    if (!window.confirm(`${t("contract.reset")}?`)) return;
    setBusy(true);
    try {
      await API.delete(`/shop/loans/${contract.loan.id}/contract`);
      await reloadContract();
      setEditContract(false);
      toast.success(t("msg.saved"));
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const preview = useMemo(() => {
    const p = Number(form.principal) || 0;
    const interest = (p * (Number(form.monthly_interest_rate) || 0) * (Number(form.term_months) || 0)) / 100;
    return { interest, total: p + interest };
  }, [form]);

  const submitLoan = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: created } = await API.post("/shop/loans", {
        ...form,
        ...termsPayload(terms),
        principal: Number(form.principal),
        monthly_interest_rate: Number(form.monthly_interest_rate),
        term_months: Number(form.term_months),
        items: items.map(({ photo_file, ...i }) => ({ ...i, estimated_value: Number(i.estimated_value) || 0, weight_grams: i.weight_grams === "" || i.weight_grams == null ? null : Number(i.weight_grams) })),
      });
      const uploads = items.map((it, idx) => it.photo_file && created.item_ids?.[idx]
        ? (() => { const fd = new FormData(); fd.append("file", it.photo_file); return API.post(`/shop/items/${created.item_ids[idx]}/photo`, fd); })()
        : null).filter(Boolean);
      if (uploads.length) {
        try { await Promise.all(uploads); toast.success(t("msg.photos_uploaded")); } catch (err) { toast.error(fmtErr(err)); }
      }
      toast.success(t("msg.loan_created"));
      setCreateOpen(false);
      setForm({ ...EMPTY_LOAN, monthly_interest_rate: shopCfg?.interest_default_rate || 10 });
      setItems([{ ...EMPTY_ITEM }]);
      setTerms(EMPTY_TERMS);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const saveTerms = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await API.patch(`/shop/loans/${detail.id}/terms`, termsPayload(termsDraft));
      setDetail((d) => ({ ...d, ...data, items: d.items, payments: d.payments }));
      toast.success(t("msg.saved"));
      setTermsOpen(false);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const onCustomerChange = (v) => {
    setForm({ ...form, customer_id: v });
    const c = customers.find((x) => x.id === v);
    if (c) setTerms((tm) => ({ ...tm, borrower_kind: c.customer_kind === "company" ? "company" : "individual" }));
  };

  const recordPayment = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await API.post(`/shop/loans/${detail.id}/payments`, { amount: Number(payAmount), method: payMethod, note: payNote || undefined });
      toast.success(t("msg.payment_recorded"));
      setDetail((d) => ({ ...d, ...data.loan, items: d.items, payments: [data.payment, ...d.payments] }));
      setPaySummary({ payment: data.payment, loan: data.loan });
      setPayAmount("");
      setPayNote("");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const extend = async () => {
    try {
      const { data } = await API.post(`/shop/loans/${detail.id}/extend`, { months: 1 });
      setDetail((d) => ({ ...d, ...data }));
      toast.success(t("msg.saved"));
      load();
      const add = await API.get(`/shop/loans/${detail.id}/extension-addendum`);
      setAddendum(add.data);
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const forfeit = async () => {
    if (!window.confirm(t("loans.forfeit") + "?")) return;
    try {
      const { data } = await API.post(`/shop/loans/${detail.id}/forfeit`);
      setDetail((d) => ({ ...d, ...data }));
      toast.success(t("msg.saved"));
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const openContract = async (loanId) => {
    try {
      const { data } = await API.get(`/shop/loans/${loanId}/contract`);
      setContract(data);
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const openBill = async (loanId) => {
    try {
      const { data } = await API.get(`/shop/loans/${loanId}/bill`);
      setBill(data);
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const sendToAuction = async () => {
    if (!window.confirm(t("auctions.send") + "?")) return;
    try {
      const { data } = await API.post(`/shop/loans/${detail.id}/auction`);
      setDetail((d) => ({ ...d, ...data }));
      toast.success(t("msg.sent_auction"));
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const onItemCategory = (idx, category) => {
    setItems(items.map((x, i) => (i === idx ? { ...x, category } : x)));
    if (idx === 0) {
      const shopRate = shopCfg?.interest_category_rates?.[category];
      const rate = shopRate ?? CATEGORY_RATES[category];
      if (rate) setForm((f) => ({ ...f, monthly_interest_rate: rate }));
    }
  };

  return (
    <div data-testid="loans-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">{t("loans.title")}</h1>
        <div className="flex gap-2 flex-wrap">
          <Select value={regime} onValueChange={setRegime}>
            <SelectTrigger data-testid="loans-regime-filter" className="w-44 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">{t("flt.regime")}: {t("flt.all")}</SelectItem>
              <SelectItem value="personal" data-testid="loans-regime-personal">{t("ct.use_personal")}</SelectItem>
              <SelectItem value="professional" data-testid="loans-regime-professional">{t("ct.use_professional")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={borrower} onValueChange={setBorrower}>
            <SelectTrigger data-testid="loans-borrower-filter" className="w-40 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">{t("flt.borrower")}: {t("flt.all")}</SelectItem>
              <SelectItem value="individual" data-testid="loans-borrower-individual">{t("ct.individual")}</SelectItem>
              <SelectItem value="company" data-testid="loans-borrower-company">{t("ct.company")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="loans-status-filter" className="w-40 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All</SelectItem>
              {["active", "overdue", "redeemed", "forfeited", "auction", "closed"].map((s) => (
                <SelectItem key={s} value={s} data-testid={`loans-filter-${s}`}>{t(`status.${s}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {managerUp && (
            <Button data-testid="create-loan-button" onClick={() => setCreateOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Plus className="h-4 w-4 mr-1.5" />{t("loans.new")}
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("loans.number")}</TableHead>
              <TableHead>{t("loans.customer")}</TableHead>
              <TableHead>{t("rpt.regime")}</TableHead>
              <TableHead>{t("loans.principal")}</TableHead>
              <TableHead>{t("loans.rate")}</TableHead>
              <TableHead>{t("loans.due")}</TableHead>
              <TableHead>{t("loans.total_due")}</TableHead>
              <TableHead>{t("common.status")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loans.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-10">{t("common.none")}</TableCell></TableRow>
            )}
            {loans.map((l) => (
              <TableRow key={l.id} data-testid={`loan-row-${l.loan_number}`} onClick={() => openDetail(l.id)} className="cursor-pointer hover:bg-slate-50">
                <TableCell className="font-mono font-semibold">{l.loan_number}</TableCell>
                <TableCell>{l.customer_name}</TableCell>
                <TableCell><span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700" data-testid={`loan-regime-${l.loan_number}`}>{l.item_use === "professional" ? "PRO" : "PERS"} · {l.borrower_kind === "company" ? t("ct.company") : t("ct.individual")}</span></TableCell>
                <TableCell className="font-mono">{money(l.principal)}</TableCell>
                <TableCell>{l.monthly_interest_rate}%</TableCell>
                <TableCell>{fmtDate(l.due_date)}</TableCell>
                <TableCell className="font-mono font-semibold text-emerald-700">{money(l.total_due)}</TableCell>
                <TableCell><StatusBadge status={l.effective_status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-loan-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("loans.new")}</DialogTitle></DialogHeader>
          <form onSubmit={submitLoan} className="space-y-5">
            <div>
              <Label>{t("loans.customer")}</Label>
              <Select value={form.customer_id} onValueChange={onCustomerChange}>
                <SelectTrigger data-testid="loan-customer-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {customers.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`loan-customer-${c.id.slice(0, 8)}`}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>{t("loans.principal")}</Label>
                <Input data-testid="loan-principal-input" type="number" min="1" step="0.01" required value={form.principal}
                  onChange={(e) => setForm({ ...form, principal: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>{t("loans.rate")}</Label>
                <Input data-testid="loan-rate-input" type="number" min={shopCfg?.interest_min_rate ?? 0.1} max={shopCfg?.interest_max_rate ?? 100} step="0.1" required value={form.monthly_interest_rate}
                  onChange={(e) => setForm({ ...form, monthly_interest_rate: e.target.value })} className="mt-1" />
                {(shopCfg?.interest_min_rate != null || shopCfg?.interest_max_rate != null) && (
                  <p className="text-[11px] text-slate-400 mt-1" data-testid="rate-band-hint">
                    {t("loans.rate_allowed")}: {shopCfg.interest_min_rate ?? 0}% – {shopCfg.interest_max_rate ?? 100}%
                  </p>
                )}
                {form.monthly_interest_rate !== "" && Number(form.monthly_interest_rate) > 0 && (
                  <p className="text-[11px] text-emerald-600 mt-1" data-testid="rate-source-label">
                    {t("loans.rate_used")}: {form.monthly_interest_rate}% ({(() => {
                      const rate = Number(form.monthly_interest_rate);
                      const cat = items?.[0]?.category;
                      const catRate = cat && shopCfg?.interest_category_rates?.[cat];
                      if (catRate && Number(catRate) === rate) return t("loans.rate_src_category");
                      if (shopCfg?.interest_default_rate && Number(shopCfg.interest_default_rate) === rate) return t("loans.rate_src_default");
                      if (cat && CATEGORY_RATES[cat] === rate) return t("loans.rate_src_suggested");
                      return t("loans.rate_src_custom");
                    })()})
                  </p>
                )}
              </div>
              <div>
                <Label>{t("loans.term")}</Label>
                <Input data-testid="loan-term-input" type="number" min="1" max="60" required value={form.term_months}
                  onChange={(e) => setForm({ ...form, term_months: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 grid grid-cols-2 gap-2 text-sm">
              <span className="text-amber-800">{t("reports.interest_earned")}</span>
              <span className="font-mono font-semibold text-amber-900 text-right" data-testid="loan-preview-interest">{money(preview.interest)}</span>
              <span className="text-amber-800">{t("loans.total_due")}</span>
              <span className="font-mono font-bold text-amber-900 text-right" data-testid="loan-preview-total">{money(preview.total)}</span>
            </div>
            <ContractTermsFields value={terms} onChange={setTerms} />
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>{t("loans.items")}</Label>
                <Button type="button" variant="outline" size="sm" data-testid="loan-add-item-button"
                  onClick={() => setItems([...items, { ...EMPTY_ITEM }])}>
                  <Plus className="h-3.5 w-3.5 mr-1" />{t("loans.add_item")}
                </Button>
              </div>
              <div className="space-y-3">
                {items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-6 gap-2 items-end border border-slate-200 rounded-lg p-3" data-testid={`loan-item-row-${idx}`}>
                    <div className="col-span-2">
                      <Label className="text-xs">{t("common.name")}</Label>
                      <Input data-testid={`loan-item-name-${idx}`} required value={it.name}
                        onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x)))} className="mt-1 h-9" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">{t("items.category")}</Label>
                      <Select value={it.category} onValueChange={(v) => onItemCategory(idx, v)}>
                        <SelectTrigger data-testid={`loan-item-category-${idx}`} className="mt-1 h-9 bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1">
                      <Label className="text-xs">{t("items.est_value")}</Label>
                      <Input data-testid={`loan-item-value-${idx}`} type="number" min="0" step="0.01" value={it.estimated_value}
                        onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, estimated_value: e.target.value } : x)))} className="mt-1 h-9" />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <button type="button" disabled={items.length === 1} onClick={() => setItems(items.filter((_, i) => i !== idx))}
                        className="p-2 hover:bg-red-50 rounded-lg disabled:opacity-30">
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </button>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">{t("items.serial")}</Label>
                      <Input data-testid={`loan-item-serial-${idx}`} value={it.serial_no}
                        onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, serial_no: e.target.value } : x)))} className="mt-1 h-9" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">{t("items.condition")}</Label>
                      <Input value={it.condition}
                        onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, condition: e.target.value } : x)))} className="mt-1 h-9" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">{t("items.location")}</Label>
                      <Input value={it.storage_location}
                        onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, storage_location: e.target.value } : x)))} className="mt-1 h-9" />
                    </div>
                    <div className="col-span-6 flex items-center gap-3">
                      <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 text-xs hover:bg-slate-50" data-testid={`loan-item-photo-label-${idx}`}>
                        <Camera className="h-3.5 w-3.5" />{t("items.photo_capture")}
                        <input type="file" accept="image/*" capture="environment" className="hidden" data-testid={`loan-item-photo-${idx}`}
                          onChange={(e) => { const f = e.target.files?.[0] || null; setItems(items.map((x, i) => (i === idx ? { ...x, photo_file: f } : x))); }} />
                      </label>
                      {it.photo_file && (
                        <span className="flex items-center gap-2 text-xs text-emerald-700" data-testid={`loan-item-photo-ready-${idx}`}>
                          <img src={URL.createObjectURL(it.photo_file)} alt="" className="h-9 w-9 rounded object-cover border border-slate-200" />
                          {t("items.photo_ready")}
                        </span>
                      )}
                    </div>
                    {VEHICLE_CATS.includes(it.category) && (
                      <>
                        <div className="col-span-2">
                          <Label className="text-xs">{t("items.brand")}</Label>
                          <Input data-testid={`loan-item-brand-${idx}`} value={it.brand}
                            onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, brand: e.target.value } : x)))} className="mt-1 h-9" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">{t("items.model")}</Label>
                          <Input value={it.model}
                            onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, model: e.target.value } : x)))} className="mt-1 h-9" />
                        </div>
                        <div className="col-span-1">
                          <Label className="text-xs">{t("items.year")}</Label>
                          <Input value={it.year}
                            onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, year: e.target.value } : x)))} className="mt-1 h-9" />
                        </div>
                        <div className="col-span-1">
                          <Label className="text-xs">{t("items.color")}</Label>
                          <Input value={it.color}
                            onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, color: e.target.value } : x)))} className="mt-1 h-9" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">{t("items.plate")}</Label>
                          <Input value={it.plate_no}
                            onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, plate_no: e.target.value } : x)))} className="mt-1 h-9" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">{t("items.chassis")}</Label>
                          <Input value={it.chassis_no}
                            onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, chassis_no: e.target.value } : x)))} className="mt-1 h-9" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">{t("items.fuel")}</Label>
                          <Input value={it.fuel_percent}
                            onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, fuel_percent: e.target.value } : x)))} className="mt-1 h-9" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">{t("items.engine")}</Label>
                          <Input data-testid={`loan-item-engine-${idx}`} value={it.engine_no}
                            onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, engine_no: e.target.value } : x)))} className="mt-1 h-9" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">{t("items.dntt")}</Label>
                          <Input value={it.dntt_ref}
                            onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, dntt_ref: e.target.value } : x)))} className="mt-1 h-9" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">{t("items.accessories")}</Label>
                          <Input value={it.accessories}
                            onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, accessories: e.target.value } : x)))} className="mt-1 h-9" />
                        </div>
                        {it.category === "Heavy Equipment" && (
                          <>
                            <div className="col-span-2">
                              <Label className="text-xs">{t("items.hours")}</Label>
                              <Input value={it.operating_hours}
                                onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, operating_hours: e.target.value } : x)))} className="mt-1 h-9" />
                            </div>
                            <div className="col-span-4">
                              <Label className="text-xs">{t("items.maintenance")}</Label>
                              <Input value={it.last_maintenance}
                                onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, last_maintenance: e.target.value } : x)))} className="mt-1 h-9" />
                            </div>
                          </>
                        )}
                      </>
                    )}
                    {it.category === "Electronics" && (
                      <>
                        <div className="col-span-2">
                          <Label className="text-xs">{t("items.imei")}</Label>
                          <Input data-testid={`loan-item-imei-${idx}`} value={it.imei}
                            onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, imei: e.target.value } : x)))} className="mt-1 h-9" />
                        </div>
                        <label className="col-span-2 flex items-center gap-2 text-xs pb-2">
                          <input type="checkbox" checked={!!it.backup_done} onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, backup_done: e.target.checked } : x)))} />{t("items.backup_done")}
                        </label>
                        <label className="col-span-2 flex items-center gap-2 text-xs pb-2">
                          <input type="checkbox" data-testid={`loan-item-wiped-${idx}`} checked={!!it.data_wiped} onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, data_wiped: e.target.checked } : x)))} />{t("items.data_wiped")}
                        </label>
                      </>
                    )}
                    {it.category === "Jewelry" && (
                      <>
                        <div className="col-span-2">
                          <Label className="text-xs">{t("items.jewelry_type")}</Label>
                          <Select value={it.jewelry_type} onValueChange={(v) => setItems(items.map((x, i) => (i === idx ? { ...x, jewelry_type: v } : x)))}>
                            <SelectTrigger data-testid={`loan-item-jewelry-type-${idx}`} className="mt-1 h-9 bg-white"><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent className="bg-white">{JEWELRY_TYPES.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">{t("items.karat")}</Label>
                          <Select value={it.gold_karat} onValueChange={(v) => setItems(items.map((x, i) => (i === idx ? { ...x, gold_karat: v } : x)))}>
                            <SelectTrigger data-testid={`loan-item-karat-${idx}`} className="mt-1 h-9 bg-white"><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent className="bg-white">{KARATS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">{t("items.weight")}</Label>
                          <Input data-testid={`loan-item-weight-${idx}`} type="number" min="0" step="0.01" value={it.weight_grams}
                            onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, weight_grams: e.target.value } : x)))} className="mt-1 h-9" />
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>{t("common.cancel")}</Button>
              <Button data-testid="loan-submit-button" type="submit" disabled={busy || !form.customer_id} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {busy ? t("common.loading") : t("common.create")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && closeDetail()}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="loan-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-3">
                  <span className="font-mono">{detail.loan_number}</span>
                  <StatusBadge status={detail.effective_status} />
                  <button data-testid="view-bill-button" onClick={() => openBill(detail.id)}
                    className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 hover:bg-slate-50 transition-colors">
                    <FileText className="h-3.5 w-3.5" />{t("bill.title")}
                  </button>
                  <button data-testid="view-contract-button" onClick={() => openContract(detail.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 hover:bg-slate-50 transition-colors">
                    <FileText className="h-3.5 w-3.5" />{t("loans.contract")}
                  </button>
                  <button data-testid="print-bundle-button" onClick={openBundle} disabled={busy}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white transition-colors">
                    <FileText className="h-3.5 w-3.5" />{t("bundle.button")}
                  </button>
                  {managerUp && (
                    <button data-testid="edit-terms-button" onClick={() => { setTermsDraft(termsFromLoan(detail)); setTermsOpen(true); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 hover:bg-slate-50 transition-colors">
                      <Pencil className="h-3.5 w-3.5" />{t("ct.edit_terms")}
                    </button>
                  )}
                  {detail.extensions?.length > 0 && (
                    <button data-testid="addendum-button" onClick={openAddendum}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 hover:bg-slate-50 transition-colors">
                      <FileText className="h-3.5 w-3.5" />{t("ext.addendum")} ({detail.extensions.length})
                    </button>
                  )}
                  {managerUp && (detail.effective_status === "overdue" || detail.status === "forfeited") && (
                    <button data-testid="auction-loan-button" onClick={sendToAuction}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-purple-300 text-purple-700 hover:bg-purple-50 transition-colors">
                      <Gavel className="h-3.5 w-3.5" />{t("auctions.send")}
                    </button>
                  )}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="text-slate-500">{t("loans.customer")}</div><div className="font-medium">{detail.customer_name}</div>
                <div className="text-slate-500">{t("loans.principal")}</div><div className="font-mono">{money(detail.principal)}</div>
                <div className="text-slate-500">{t("loans.rate")}</div><div>{detail.monthly_interest_rate}% × {detail.term_months}</div>
                <div className="text-slate-500">{t("loans.due")}</div><div>{fmtDate(detail.due_date)}</div>
                <div className="text-slate-500">{t("ct.item_use")}</div><div data-testid="loan-detail-item-use">{t(`ct.use_${detail.item_use || "personal"}`)} · {t(`ct.${detail.borrower_kind || "individual"}`)}</div>
                <div className="text-slate-500">{t("loans.interest_due")}</div><div className="font-mono text-amber-700">{money(detail.interest_due)}</div>
                <div className="text-slate-500">{t("loans.principal_due")}</div><div className="font-mono">{money(detail.principal_due)}</div>
                <div className="text-slate-500 font-semibold">{t("loans.total_due")}</div>
                <div className="font-mono font-bold text-emerald-700" data-testid="loan-detail-total-due">{money(detail.total_due)}</div>
              </div>
              <div className="mt-4">
                <h4 className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">{t("loans.items")}</h4>
                <div className="space-y-1.5">
                  {detail.items?.map((i) => (
                    <div key={i.id} className="flex justify-between text-sm border border-slate-200 rounded-lg px-3 py-2">
                      <span>{i.name} <span className="text-slate-400">· {i.category}</span></span>
                      <StatusBadge status={i.status} />
                    </div>
                  ))}
                </div>
              </div>
              {detail.status === "active" && (
                <form onSubmit={recordPayment} className="mt-5 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-emerald-900 mb-1">{t("loans.record_payment")}</h4>
                  <p className="text-xs text-emerald-700 mb-3">{t("loans.redeem_hint")}</p>
                  <div className="grid grid-cols-4 gap-2">
                    <Select value={payType} onValueChange={(v) => {
                      setPayType(v);
                      if (v === "interest") setPayAmount(String(detail.interest_due));
                      if (v === "full") setPayAmount(String(detail.total_due));
                    }}>
                      <SelectTrigger data-testid="payment-type-select" className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="custom" data-testid="pay-type-custom">{t("pay.custom")}</SelectItem>
                        <SelectItem value="interest" data-testid="pay-type-interest">{t("pay.interest_only")}</SelectItem>
                        <SelectItem value="full" data-testid="pay-type-full">{t("pay.full")}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input data-testid="payment-amount-input" type="number" min="0.01" step="0.01" required value={payAmount}
                      onChange={(e) => { setPayAmount(e.target.value); setPayType("custom"); }} placeholder={money(detail.total_due)} className="bg-white" />
                    <Select value={payMethod} onValueChange={setPayMethod}>
                      <SelectTrigger data-testid="payment-method-select" className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="bank_transfer">{t("settings.subscription") === "Subscription" ? "Bank transfer" : "Transferénsia"}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder={t("common.notes")} className="bg-white" />
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button data-testid="record-payment-button" type="submit" disabled={busy} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      {t("loans.record_payment")}
                    </Button>
                    <Button type="button" size="sm" variant="outline" data-testid="pay-full-button" onClick={() => setPayAmount(String(detail.total_due))}>
                      {money(detail.total_due)}
                    </Button>
                    <div className="flex-1" />
                    {managerUp && (
                      <>
                        <Button type="button" size="sm" variant="outline" data-testid="extend-loan-button" onClick={extend}>+1 {t("loans.extend")}</Button>
                        <Button type="button" size="sm" variant="outline" data-testid="forfeit-loan-button" onClick={forfeit}
                          className="text-red-600 border-red-200 hover:bg-red-50">{t("loans.forfeit")}</Button>
                      </>
                    )}
                  </div>
                </form>
              )}
              <div className="mt-5" data-testid="loan-message-log">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="text-xs font-mono uppercase tracking-wider text-slate-500">{t("msgs.title")}</h4>
                  <div className="flex-1" />
                  <button data-testid="portal-link-button" onClick={copyPortal}
                    className="text-xs px-2.5 py-1 rounded-lg border border-slate-300 hover:bg-slate-50 flex items-center gap-1"><Link2 className="h-3 w-3" />{t("portal.copy")}</button>
                </div>
                <MessageList rows={loanMessages} compact testid="loan-messages" />
              </div>
              <div className="mt-5" data-testid="notice-history">
                <h4 className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">{t("ntc.history")}</h4>
                {!detail.notices?.length && <p className="text-sm text-slate-500">{t("ntc.none")}</p>}
                <div className="space-y-1.5">
                  {detail.notices?.map((n) => (
                    <div key={n.id} className="flex justify-between items-center text-sm border border-slate-200 rounded-lg px-3 py-2" data-testid={`notice-row-${n.id.slice(0, 8)}`}>
                      <span>
                        <span className="font-mono text-xs text-slate-500">{fmtDate(n.created_at)}</span> · {t(`ntc.${n.kind}`)}
                        <span className="text-slate-400 text-xs"> · {n.notice_no} · {n.created_by}</span>
                        {n.shared_at && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-800">{t("ntc.shared")}</span>}
                      </span>
                      <span className="flex items-center gap-2 text-xs">
                        <span className="text-slate-500">{t("ntc.deadline")}: <span className="font-mono">{n.deadline}</span></span>
                        <a href={`${API.defaults.baseURL}/shop/notice-public/${n.token}`} target="_blank" rel="noreferrer" className="text-emerald-700 underline" data-testid={`notice-open-${n.id.slice(0, 8)}`}>{t("ntc.open_public")}</a>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-5">
                <h4 className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">{t("loans.payments")}</h4>
                {detail.payments?.length === 0 && <p className="text-sm text-slate-500">{t("common.none")}</p>}
                <div className="space-y-1.5">
                  {detail.payments?.map((p) => (
                    <div key={p.id} className="flex justify-between text-sm border border-slate-200 rounded-lg px-3 py-2">
                      <span>{fmtDate(p.date)} <span className="text-slate-400">· {p.method}</span></span>
                      <span className="font-mono">{money(p.amount)} <span className="text-slate-400 text-xs">({t("repay.interest_part")} {money(p.interest_portion)})</span></span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <LetterDialog doc={addendum} title={t("ext.addendum")} onClose={() => setAddendum(null)} testid="addendum" />

      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="terms-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("ct.edit_terms")} · {detail?.loan_number}</DialogTitle></DialogHeader>
          <form onSubmit={saveTerms} className="space-y-4">
            <ContractTermsFields value={termsDraft} onChange={setTermsDraft} />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setTermsOpen(false)}>{t("common.cancel")}</Button>
              <Button data-testid="terms-save-button" type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white">{t("common.save")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!paySummary} onOpenChange={(o) => !o && setPaySummary(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="payment-summary-dialog">
          {paySummary && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{t("pay.summary")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="text-center bg-emerald-50 border border-emerald-200 rounded-xl py-5">
                  <div className="text-xs font-mono uppercase tracking-wider text-emerald-700">{t("pay.paid")}</div>
                  <div className="font-heading text-3xl font-bold text-emerald-700 mt-1" data-testid="summary-amount">{money(paySummary.payment.amount)}</div>
                  <div className="text-xs text-slate-500 mt-1">{paySummary.payment.loan_number} · {paySummary.payment.customer_name}</div>
                </div>
                <div className="flex justify-between"><span className="text-slate-500">{t("repay.interest_part")}</span><span className="font-mono text-amber-700">{money(paySummary.payment.interest_portion)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">{t("repay.principal_part")}</span><span className="font-mono">{money(paySummary.payment.principal_portion)}</span></div>
                <div className="flex justify-between border-t border-slate-200 pt-3"><span className="text-slate-500">{t("repay.remaining")}</span><span className="font-mono font-semibold" data-testid="summary-remaining">{money(paySummary.loan.total_due)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">{t("rcp.months_billed")}</span><span className="font-mono">{paySummary.loan.months_billed}</span></div>
                {paySummary.loan.penalty > 0 && (
                  <div className="flex justify-between"><span className="text-slate-500">{t("rcp.penalty")}</span><span className="font-mono text-red-600">{money(paySummary.loan.penalty)}</span></div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">{t("common.status")}</span>
                  <StatusBadge status={paySummary.loan.effective_status} />
                </div>
                {paySummary.loan.status === "active" && paySummary.loan.next_payment && (
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 space-y-1.5 text-sm" data-testid="summary-next-payment">
                    <div className="text-xs font-bold text-slate-700">{t("rcp.next_title")}</div>
                    <div className="flex justify-between"><span className="text-slate-600">{t("rcp.next_date")}</span><span className="font-mono font-semibold">{fmtDate(paySummary.loan.next_payment.date)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">{t("rcp.current_balance")}</span><span className="font-mono">{money(paySummary.loan.total_due)}</span></div>
                    {paySummary.loan.next_payment.type === "interest" && (
                      <>
                        <div className="flex justify-between"><span className="text-slate-600">{t("rcp.next_interest")}</span><span className="font-mono text-amber-700">+ {money(paySummary.loan.next_payment.amount)}</span></div>
                        <div className="flex justify-between font-semibold"><span>{t("rcp.if_unpaid")}</span><span className="font-mono">{money(paySummary.loan.next_payment.new_total_if_unpaid)}</span></div>
                      </>
                    )}
                  </div>
                )}
                {paySummary.loan.status === "active" && (
                  <div className="flex justify-between"><span className="text-slate-500">{t("pay.next_due")}</span><span>{fmtDate(paySummary.loan.due_date)}</span></div>
                )}
                <Button data-testid="summary-close-button" onClick={() => setPaySummary(null)} className="w-full bg-slate-900 hover:bg-slate-800 text-white mt-2">OK</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!bill} onOpenChange={(o) => !o && setBill(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bill-dialog">
          {bill && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{t("bill.title")}</DialogTitle></DialogHeader>
              <BillDoc bill={bill} printMode={printMode} draftMark={draftMark} />
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <div className="flex rounded-lg border border-slate-300 overflow-hidden text-xs">
                  <button data-testid="bill-print-mode-a4" onClick={() => setPrintMode("a4")}
                    className={`px-3 py-1.5 ${printMode === "a4" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>{t("print.mode_a4")}</button>
                  <button data-testid="bill-print-mode-thermal" onClick={() => setPrintMode("thermal")}
                    className={`px-3 py-1.5 ${printMode === "thermal" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>{t("print.mode_thermal")}</button>
                </div>
                <button data-testid="bill-print-draft-toggle" onClick={() => setDraftMark(!draftMark)}
                  className={`px-3 py-1.5 rounded-lg border text-xs ${draftMark ? "border-red-400 bg-red-50 text-red-700" : "border-slate-300 text-slate-600"}`}>
                  {t("print.draft")}
                </button>
                <Button data-testid="bill-print-button"
                  onClick={() => printDoc(document.querySelector('[data-testid="bill-body"]'), { mode: printMode, draft: draftMark, title: bill.bill_no || "BILL" })}
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white">
                  {t("repay.print")}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!contract} onOpenChange={(o) => !o && setContract(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="contract-dialog">
          {contract && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">KONTRATU PENHOR</DialogTitle>
              </DialogHeader>
              <ContractDoc contract={contract} printMode={printMode} draftMark={draftMark}
                editing={editContract} draft={contractDraft} setDraftArticle={setDraftArticle} setDraftTerm={setDraftTerm} />
              
              <div className="flex flex-wrap items-center gap-2 mt-4">
                {contract.edited && (
                  <span className="text-[11px] font-semibold px-2 py-1 rounded bg-amber-100 text-amber-800" data-testid="contract-edited-badge">{t("contract.edited")}</span>
                )}
                <div className="flex rounded-lg border border-slate-300 overflow-hidden text-xs">
                  <button data-testid="print-mode-a4" onClick={() => setPrintMode("a4")}
                    className={`px-3 py-1.5 ${printMode === "a4" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>{t("print.mode_a4")}</button>
                  <button data-testid="print-mode-thermal" onClick={() => setPrintMode("thermal")}
                    className={`px-3 py-1.5 ${printMode === "thermal" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>{t("print.mode_thermal")}</button>
                </div>
                <button data-testid="print-draft-toggle" onClick={() => setDraftMark(!draftMark)}
                  className={`px-3 py-1.5 rounded-lg border text-xs ${draftMark ? "border-red-400 bg-red-50 text-red-700" : "border-slate-300 text-slate-600"}`}>
                  {t("print.draft")}
                </button>
                {managerUp && !editContract && (
                  <Button variant="outline" size="sm" data-testid="contract-edit-button" onClick={startContractEdit}>{t("contract.edit")}</Button>
                )}
                {managerUp && editContract && (
                  <>
                    <Button size="sm" data-testid="contract-save-button" onClick={saveContract} disabled={busy}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white">{t("common.save")}</Button>
                    <Button variant="outline" size="sm" data-testid="contract-reset-button" onClick={resetContract} disabled={busy}>{t("contract.reset")}</Button>
                    <Button variant="outline" size="sm" onClick={() => setEditContract(false)}>{t("common.cancel")}</Button>
                  </>
                )}
                <Button data-testid="contract-print-button"
                  onClick={() => printDoc(document.querySelector('[data-testid="contract-body"]'), { mode: printMode, draft: draftMark, title: contract.contract_no })}
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white">
                  {t("repay.print")}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!bundle} onOpenChange={(o) => !o && setBundle(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bundle-dialog">
          {bundle && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{t("bundle.title")}</DialogTitle></DialogHeader>
              <div className="space-y-6">
                <div data-testid="bundle-contract" className="border border-slate-200 rounded-lg p-4">
                  <ContractDoc contract={bundle.contract} printMode={printMode} draftMark={draftMark} />
                </div>
                <div data-testid="bundle-bill" className="border border-slate-200 rounded-lg p-4">
                  <BillDoc bill={bundle.bill} printMode={printMode} draftMark={draftMark} />
                </div>
                {bundle.receipt && (
                  <div data-testid="bundle-receipt" className="border border-slate-200 rounded-lg p-4">
                    <ReceiptDoc receipt={bundle.receipt} printMode={printMode} draftMark={draftMark} />
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <div className="flex rounded-lg border border-slate-300 overflow-hidden text-xs">
                  <button data-testid="bundle-mode-a4" onClick={() => setPrintMode("a4")}
                    className={`px-3 py-1.5 ${printMode === "a4" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>{t("print.mode_a4")}</button>
                  <button data-testid="bundle-mode-thermal" onClick={() => setPrintMode("thermal")}
                    className={`px-3 py-1.5 ${printMode === "thermal" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>{t("print.mode_thermal")}</button>
                </div>
                <button data-testid="bundle-draft-toggle" onClick={() => setDraftMark(!draftMark)}
                  className={`px-3 py-1.5 rounded-lg border text-xs ${draftMark ? "border-red-400 bg-red-50 text-red-700" : "border-slate-300 text-slate-600"}`}>
                  {t("print.draft")}
                </button>
                <Button data-testid="bundle-print-button"
                  onClick={() => printDoc([
                    document.querySelector('[data-testid="bundle-contract"] [data-testid="contract-body"]'),
                    document.querySelector('[data-testid="bundle-bill"] [data-testid="bill-body"]'),
                    document.querySelector('[data-testid="bundle-receipt"] [data-testid="receipt-body"]'),
                  ], { mode: printMode, draft: draftMark, title: `BUNDLE-${detail.loan_number}` })}
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white">
                  {t("repay.print")}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
